import type {
  RouteLocationRaw,
  RouteLocationNormalizedGeneric,
} from 'vue-router'

/**
 * Extracts the query string from a URL or route object
 * @param url - URL string or route object
 * @returns Query string with leading '?' or empty string
 *
 * @example
 * getQuery('/shop/product?id=123') // Returns '?id=123'
 * getQuery({ path: '/shop', query: { id: '123' } }) // Returns '?id=123'
 */
export function getQuery(
  url: RouteLocationRaw | RouteLocationNormalizedGeneric
): string {
  if (typeof url === 'string') {
    try {
      // Full URL? Parse it and get the search params
      return new URL(url).search
    } catch {
      // It's a relative path
      // Ignore the http://localhost - this is only to make the URL constructor happy
      return new URL(url, 'http://localhost').search
    }
  }

  if ('query' in url && url.query) {
    const query = new URLSearchParams(
      Object.fromEntries(
        Object.entries(url.query).map(([k, v]) => [k, v ? String(v) : ''])
      )
    ).toString()

    return query ? `?${query}` : ''
  }

  return ''
}

/**
 * Extracts the pathname from a URL or route object
 * @param url - URL string or route object
 * @returns Pathname without query string
 *
 * @example
 * getPath('https://example.com/shop/product') // Returns '/shop/product'
 * getPath('/shop/product?id=123') // Returns '/shop/product'
 */
export function getPath(
  url: RouteLocationRaw | RouteLocationNormalizedGeneric
): string {
  if (typeof url === 'string') {
    try {
      // Full URL? Parse it and get the pathname
      return new URL(url).pathname
    } catch {
      // It's a relative path
      // Ignore the http://localhost - this is only to make the URL constructor happy
      return new URL(url, 'http://localhost').pathname
    }
  }

  return url.path ?? ''
}

/**
 * Extracts the fragment from a URL or route object
 * @param url - URL string or route object
 * @returns Hash with leading '#' or empty string
 *
 * @example
 * getHash('/shop/product#details') // Returns '#details'
 * getHash({ path: '/shop', hash: '#details' }) // Returns '#details'
 */
export function getHash(
  url: RouteLocationRaw | RouteLocationNormalizedGeneric
): string {
  if (typeof url === 'string') {
    try {
      return new URL(url).hash
    } catch {
      // Ignore the http://localhost - this is only to make the URL constructor happy
      return new URL(url, 'http://localhost').hash
    }
  }

  return ('hash' in url && url.hash) || ''
}

/**
 * Converts a route object to a URL string if possible
 * @param route - Route object or string
 * @returns URL string or undefined
 */
export function getUrl(
  route: RouteLocationRaw | RouteLocationNormalizedGeneric
): string | undefined {
  if (typeof route === 'string') {
    return route
  }

  if ('to' in route) {
    return route.to as string
  }

  return route.toString()
}
